/**
 * Gestion du temps NBA.
 *  - Une « date NBA » est la date de programmation d'un match, exprimée dans le fuseau America/New_York.
 *    Un match programmé le 1er mars à 22 h 30 (heure de la côte Ouest) se termine vers 4 h du matin à
 *    Paris le 2 mars : il reste rattaché au 1er mars. On ne dérive jamais la date NBA de l'heure de Paris.
 *  - L'affichage se fait en Europe/Paris.
 */

export const NBA_TZ = "America/New_York";
export const DISPLAY_TZ = "Europe/Paris";

const ymdFormatter = (tz: string) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });

/** Date NBA (AAAA-MM-JJ, fuseau de New York) correspondant à un instant. */
export function toNbaDate(instant: Date = new Date()): string {
  return ymdFormatter(NBA_TZ).format(instant);
}

export function nbaToday(): string {
  return toNbaDate(new Date());
}

export function isYmd(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

export function addDays(ymd: string, days: number): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(fromYmd: string, toYmd: string): number {
  const a = new Date(`${fromYmd}T12:00:00Z`).getTime();
  const b = new Date(`${toYmd}T12:00:00Z`).getTime();
  return Math.round((b - a) / 86_400_000);
}

export function dateRange(fromYmd: string, toYmd: string): string[] {
  const n = daysBetween(fromYmd, toYmd);
  if (n < 0) return [];
  return Array.from({ length: n + 1 }, (_, i) => addDays(fromYmd, i));
}

/**
 * Saison NBA par défaut pour une date (convention : 2025 = saison 2025-26).
 * Septembre ou plus tard → saison qui commence cette année-là.
 * Sert uniquement de valeur par défaut d'interface : la saison réelle d'un match est fournie par la source.
 */
export function seasonForDate(ymd: string): number {
  const [y, m] = ymd.split("-").map(Number);
  return m >= 9 ? y : y - 1;
}

export function currentSeason(): number {
  return seasonForDate(nbaToday());
}

export function seasonLabel(season: number): string {
  return `${season}-${String((season + 1) % 100).padStart(2, "0")}`;
}

export function parseSeasonParam(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{4})/.exec(value);
  if (!m) return null;
  const y = Number(m[1]);
  return y >= 1946 && y <= 2100 ? y : null;
}

const parisTime = new Intl.DateTimeFormat("fr-FR", { timeZone: DISPLAY_TZ, hour: "2-digit", minute: "2-digit" });
const parisDateTime = new Intl.DateTimeFormat("fr-FR", {
  timeZone: DISPLAY_TZ, weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
});
const parisFull = new Intl.DateTimeFormat("fr-FR", {
  timeZone: DISPLAY_TZ, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});

export function formatParisTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : parisTime.format(d);
}

export function formatParisDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : parisDateTime.format(d);
}

export function formatParisStamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : parisFull.format(d).replace(",", " à");
}

/** Affiche une date NBA (AAAA-MM-JJ) en toutes lettres, sans conversion de fuseau. */
export function formatNbaDate(ymd: string, opts: Intl.DateTimeFormatOptions = { weekday: "long", day: "numeric", month: "long", year: "numeric" }): string {
  return new Intl.DateTimeFormat("fr-FR", { ...opts, timeZone: "UTC" }).format(new Date(`${ymd}T12:00:00Z`));
}
